import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { INDUSTRY_LIST, INDUSTRIES } from "@/content/solutions";
import { SemaLogo } from "@/components/marketing/SemaLogo";

function Icon({ name, ...props }) {
  const C = Icons[name] || Icons.Square;
  return <C {...props} />;
}

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false); // industries dropdown
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenu(false); }, [pathname]);

  const goPricing = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      document.querySelector("#pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/");
      setTimeout(() => document.querySelector("#pricing")?.scrollIntoView({ behavior: "smooth" }), 250);
    }
  };

  const linkCls = "font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors";

  return (
    <header
      data-testid="marketing-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/80 backdrop-blur-md border-b border-border/60" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="marketing-nav-brand" className="inline-flex items-center">
          <SemaLogo to={null} imgClassName="h-8" testid="marketing-nav-logo" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          <Link to="/product" data-testid="marketing-nav-product" className={linkCls}>Product</Link>

          {/* Industries mega-dropdown */}
          <div className="relative" onMouseEnter={() => setMenu(true)} onMouseLeave={() => setMenu(false)}>
            <button
              type="button"
              data-testid="industries-dropdown-trigger"
              onClick={() => setMenu((v) => !v)}
              className={`inline-flex items-center gap-1.5 ${linkCls}`}
            >
              Industries <ChevronDown className={`h-3.5 w-3.5 transition-transform ${menu ? "rotate-180" : ""}`} />
            </button>
            {menu && (
              <div
                data-testid="industries-dropdown-panel"
                className="absolute left-1/2 top-full z-50 w-[34rem] -translate-x-1/2 pt-3"
              >
                <div className="grid grid-cols-2 gap-1.5 rounded-2xl border border-border/60 bg-card/95 p-3 shadow-2xl shadow-black/40 backdrop-blur-xl">
                  {INDUSTRY_LIST.map((slug) => {
                    const ind = INDUSTRIES[slug];
                    return (
                      <Link
                        key={slug}
                        to={`/solutions/${slug}`}
                        data-testid={`industries-dropdown-item-${slug}`}
                        className="group flex items-start gap-3 rounded-xl border border-transparent p-3 transition-colors hover:border-border/70 hover:bg-background/60"
                      >
                        <span
                          className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                          style={{ backgroundColor: `${ind.accent}1f`, color: ind.accent }}
                        >
                          <Icon name={ind.icon} className="h-4.5 w-4.5" />
                        </span>
                        <span>
                          <span className="block font-mono text-sm font-semibold tracking-tight text-foreground">{ind.name}</span>
                          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{ind.navBlurb}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <a href="/#pricing" onClick={goPricing} data-testid="marketing-nav-pricing" className={linkCls}>Pricing</a>
          <Link to="/blog" data-testid="marketing-nav-blog" className={linkCls}>Blog</Link>
          <Link to="/docs" data-testid="marketing-nav-docs" className={linkCls}>Docs</Link>
          <Link to="/contact" data-testid="marketing-nav-contact" className={linkCls}>Contact</Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="marketing-nav-signin" className={`hidden md:inline-block ${linkCls}`}>Sign in</Link>
          <Link to="/signup" className="hidden md:inline-block">
            <Button data-testid="marketing-nav-cta" size="sm"
              className="rounded-full sema-cta-gradient px-4 font-mono text-xs uppercase tracking-[0.18em] text-white shadow-lg shadow-sema-primary/25 transition-transform hover:-translate-y-0.5">
              Get started
            </Button>
          </Link>
          <ThemeToggle testId="marketing-nav-theme-toggle" />
          <button
            type="button"
            data-testid="marketing-nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground hover:text-foreground md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="marketing-nav-mobile" className="border-t border-border/60 bg-background/95 backdrop-blur-md md:hidden">
          <nav className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-6 py-4">
            <Link to="/product" className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Product</Link>
            <p className="px-2 pt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60">Industries</p>
            {INDUSTRY_LIST.map((slug) => (
              <Link key={slug} to={`/solutions/${slug}`} data-testid={`mobile-industry-${slug}`}
                className="flex items-center gap-2.5 rounded-md px-2 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                <Icon name={INDUSTRIES[slug].icon} className="h-4 w-4" style={{ color: INDUSTRIES[slug].accent }} />
                {INDUSTRIES[slug].name}
              </Link>
            ))}
            <a href="/#pricing" onClick={goPricing} className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Pricing</a>
            <Link to="/blog" className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Blog</Link>
            <Link to="/docs" className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Docs</Link>
            <Link to="/contact" className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Contact</Link>
            <Link to="/login" className="rounded-md px-2 py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted hover:text-foreground">Sign in</Link>
            <Link to="/signup" className="mt-2">
              <Button size="sm" className="w-full rounded-full sema-cta-gradient font-mono text-xs uppercase tracking-[0.18em] text-white">Get started</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
